import type {
  GraphicsBackend,
  GraphicsCapability,
  ReadonlyWebGPUInfo,
} from '@/graphics/GraphicsBackend'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import {
  GraphicsConfigConflictError,
  type GraphicsRequirementIssue,
  GraphicsRequirementsError,
} from '@/graphics/GraphicsErrors'

/** 可由插件声明的通用图形能力；值域与 GraphicsCapability 保持同步。 */
export type GraphicsCapabilityName = keyof GraphicsCapability

/** 当前 WebGPU 基线允许作为静态 device requirement 的 optional feature。 */
export const WEBGPU_DEVICE_FEATURE_NAMES = [
  'subgroups',
  'timestamp-query',
  'indirect-first-instance',
  'shader-f16',
  'float32-filterable',
  'texture-compression-bc',
] as const

export type WebGPUDeviceFeatureName = (typeof WEBGPU_DEVICE_FEATURE_NAMES)[number]

/** 3DGS compute、资源布局和 readback 在本 change 中会校验的 WebGPU limit。 */
export const WEBGPU_DEVICE_LIMIT_NAMES = [
  'maxTextureDimension2D',
  'maxBindGroups',
  'maxBindingsPerBindGroup',
  'maxStorageBuffersPerShaderStage',
  'maxStorageTexturesPerShaderStage',
  'maxUniformBufferBindingSize',
  'maxStorageBufferBindingSize',
  'minUniformBufferOffsetAlignment',
  'minStorageBufferOffsetAlignment',
  'maxBufferSize',
  'maxColorAttachments',
  'maxColorAttachmentBytesPerSample',
  'maxComputeWorkgroupStorageSize',
  'maxComputeInvocationsPerWorkgroup',
  'maxComputeWorkgroupSizeX',
  'maxComputeWorkgroupSizeY',
  'maxComputeWorkgroupSizeZ',
  'maxComputeWorkgroupsPerDimension',
  'minSubgroupSize',
  'maxSubgroupSize',
] as const

export type WebGPUDeviceLimitName = (typeof WEBGPU_DEVICE_LIMIT_NAMES)[number]

/**
 * 单项 device limit 约束。
 *
 * `minimum` 用于 maxBufferSize 等“越大越强”的 limit；`maximum` 用于 offset alignment 等
 * “越小越强”的 limit。两者可同时声明为闭区间。
 */
export interface WebGPUDeviceLimitRequirement {
  readonly name: WebGPUDeviceLimitName
  readonly minimum?: number
  readonly maximum?: number
}

/** 必须在 GPUDevice 创建前汇总的 WebGPU feature/limit。 */
export interface WebGPUDeviceRequirements {
  readonly features?: readonly WebGPUDeviceFeatureName[]
  readonly limits?: readonly WebGPUDeviceLimitRequirement[]
}

/** backend 初始化后由统一 resolver 校验的通用图形要求。 */
export interface GraphicsRequirements {
  readonly backends: readonly GraphicsBackendKind[]
  readonly capabilities?: readonly GraphicsCapabilityName[]
}

/** Plugin、RenderContributor 与动态扩展共享的声明面。 */
export interface GraphicsRequirementDeclaration {
  readonly graphicsRequirements?: GraphicsRequirements
  readonly webgpuDeviceRequirements?: WebGPUDeviceRequirements
}

export type GraphicsRequirementSubjectType =
  | 'plugin'
  | 'render-contributor'
  | 'postprocess'
  | 'graphics-extension'

export interface GraphicsRequirementSubjectIdentity {
  readonly type: GraphicsRequirementSubjectType
  readonly name: string
}

/** 未声明旧插件的迁移安全默认值。 */
export const WEBGL2_ONLY_GRAPHICS_REQUIREMENTS: GraphicsRequirements = Object.freeze({
  backends: Object.freeze(['webgl2'] as const),
})

/** 经审计确认不依赖具体图形 API 的双端声明。 */
export const DUAL_BACKEND_GRAPHICS_REQUIREMENTS: GraphicsRequirements = Object.freeze({
  backends: Object.freeze(['webgl2', 'webgpu'] as const),
})

/** WebGPU 创建前已合并、去重并校验内部冲突的 device requirements。 */
export interface ResolvedWebGPUDeviceRequirements {
  readonly features: readonly WebGPUDeviceFeatureName[]
  readonly limits: readonly WebGPUDeviceLimitRequirement[]
}

/** 传给 requestDevice 的稳定解析结果。 */
export interface WebGPUDeviceRequestResolution {
  readonly requiredFeatures: readonly WebGPUDeviceFeatureName[]
  readonly requiredLimits: Readonly<Partial<Record<WebGPUDeviceLimitName, number>>>
  readonly missingRequirements: readonly string[]
}

const EMPTY_DEVICE_REQUIREMENTS: ResolvedWebGPUDeviceRequirements = Object.freeze({
  features: Object.freeze([]),
  limits: Object.freeze([]),
})

function resolveDeclaration(subject: GraphicsRequirementDeclaration): GraphicsRequirements {
  return subject.graphicsRequirements ?? WEBGL2_ONLY_GRAPHICS_REQUIREMENTS
}

function formatLimitRequirement(requirement: WebGPUDeviceLimitRequirement): string {
  if (requirement.minimum !== undefined && requirement.maximum !== undefined) {
    return `device-limit:${requirement.name}[${requirement.minimum},${requirement.maximum}]`
  }
  if (requirement.minimum !== undefined) {
    return `device-limit:${requirement.name}>=${requirement.minimum}`
  }
  return `device-limit:${requirement.name}<=${requirement.maximum}`
}

function isLimitSatisfied(
  actual: number | undefined,
  requirement: WebGPUDeviceLimitRequirement
): boolean {
  if (actual === undefined || !Number.isFinite(actual)) return false
  if (requirement.minimum !== undefined && actual < requirement.minimum) return false
  if (requirement.maximum !== undefined && actual > requirement.maximum) return false
  return true
}

function collectMissingDeviceRequirements(
  requirements: WebGPUDeviceRequirements | undefined,
  webgpu: ReadonlyWebGPUInfo | undefined
): string[] {
  if (!requirements) return []
  const missing: string[] = []
  const deviceFeatures = new Set(webgpu?.deviceFeatures ?? [])
  for (const feature of requirements.features ?? []) {
    if (!deviceFeatures.has(feature)) {
      missing.push(`device-feature:${feature}`)
    }
  }
  for (const limit of requirements.limits ?? []) {
    if (!isLimitSatisfied(webgpu?.deviceLimits[limit.name], limit)) {
      missing.push(formatLimitRequirement(limit))
    }
  }
  return missing
}

/**
 * 尚未创建 renderer/device 时的图形能力画像。
 *
 * `capabilities` / `webgpu` 缺省表示**未知**：未知不能被当作通过，会落到
 * `unresolvedRequirements` 里由调用方呈现为「需运行时确认」。
 */
export interface RequestedGraphicsProfile {
  readonly kind: GraphicsBackendKind
  readonly capabilities?: Partial<GraphicsCapability>
  readonly webgpu?: {
    readonly deviceFeatures?: readonly string[]
    readonly deviceLimits?: Readonly<Record<string, number>>
  }
}

/**
 * 纯静态解析单个主体的不兼容信息；兼容时返回 null。
 *
 * 不创建 renderer/device、不启动插件，只读声明与调用方给出的画像。
 */
export function resolveStaticGraphicsRequirementIssue(
  subject: GraphicsRequirementDeclaration,
  profile: RequestedGraphicsProfile,
  identity: GraphicsRequirementSubjectIdentity
): GraphicsRequirementIssue | null {
  const requirements = resolveDeclaration(subject)
  const supportedBackends = Object.freeze([...requirements.backends])
  const missingCapabilities: string[] = []
  const unresolvedRequirements: string[] = []
  const backendSupported = supportedBackends.includes(profile.kind)

  if (backendSupported) {
    for (const capability of requirements.capabilities ?? []) {
      if (profile.capabilities === undefined) {
        unresolvedRequirements.push(`capability:${capability}`)
      } else if (!profile.capabilities[capability]) {
        missingCapabilities.push(capability)
      }
    }
    if (profile.kind === 'webgpu' && subject.webgpuDeviceRequirements) {
      if (profile.webgpu === undefined) {
        for (const feature of subject.webgpuDeviceRequirements.features ?? []) {
          unresolvedRequirements.push(`device-feature:${feature}`)
        }
        for (const limit of subject.webgpuDeviceRequirements.limits ?? []) {
          unresolvedRequirements.push(formatLimitRequirement(limit))
        }
      } else {
        missingCapabilities.push(
          ...collectMissingDeviceRequirements(subject.webgpuDeviceRequirements, {
            deviceFeatures: profile.webgpu.deviceFeatures ?? [],
            deviceLimits: profile.webgpu.deviceLimits ?? {},
          } as unknown as ReadonlyWebGPUInfo)
        )
      }
    }
  }

  if (backendSupported && missingCapabilities.length === 0 && unresolvedRequirements.length === 0) {
    return null
  }
  return Object.freeze({
    subjectType: identity.type,
    subjectName: identity.name,
    currentBackend: profile.kind,
    supportedBackends,
    missingCapabilities: Object.freeze(missingCapabilities),
    ...(unresolvedRequirements.length > 0
      ? { unresolvedRequirements: Object.freeze(unresolvedRequirements) }
      : {}),
  })
}

/** 按调用方给定顺序收集全部静态不兼容主体（不创建 renderer/device）。 */
export function collectStaticGraphicsRequirementIssues(
  subjects: readonly {
    readonly subject: GraphicsRequirementDeclaration
    readonly identity: GraphicsRequirementSubjectIdentity
  }[],
  profile: RequestedGraphicsProfile
): readonly GraphicsRequirementIssue[] {
  const issues: GraphicsRequirementIssue[] = []
  for (const { subject, identity } of subjects) {
    const issue = resolveStaticGraphicsRequirementIssue(subject, profile, identity)
    if (issue) issues.push(issue)
  }
  return Object.freeze(issues)
}

/** 返回单个主体的不兼容信息；兼容时返回 null。运行时实现复用静态解析核心。 */
export function resolveGraphicsRequirementIssue(
  subject: GraphicsRequirementDeclaration,
  graphics: GraphicsBackend,
  identity: GraphicsRequirementSubjectIdentity
): GraphicsRequirementIssue | null {
  const webgpu = graphics.kind === 'webgpu' ? graphics.getInfo().webgpu : undefined
  return resolveStaticGraphicsRequirementIssue(
    subject,
    {
      kind: graphics.kind,
      capabilities: graphics.capabilities,
      ...(webgpu
        ? {
            webgpu: {
              deviceFeatures: webgpu.deviceFeatures,
              deviceLimits: webgpu.deviceLimits,
            },
          }
        : {}),
    },
    identity
  )
}

/** 按调用方给定顺序收集全部不兼容主体。 */
export function collectGraphicsRequirementIssues(
  subjects: readonly {
    readonly subject: GraphicsRequirementDeclaration
    readonly identity: GraphicsRequirementSubjectIdentity
  }[],
  graphics: GraphicsBackend
): readonly GraphicsRequirementIssue[] {
  const issues: GraphicsRequirementIssue[] = []
  for (const { subject, identity } of subjects) {
    const issue = resolveGraphicsRequirementIssue(subject, graphics, identity)
    if (issue) issues.push(issue)
  }
  return Object.freeze(issues)
}

/** 校验一个动态主体，并在其进入 registry 前抛结构化错误。 */
export function assertGraphicsRequirements(
  subject: GraphicsRequirementDeclaration,
  graphics: GraphicsBackend,
  identity: GraphicsRequirementSubjectIdentity
): void {
  const issue = resolveGraphicsRequirementIssue(subject, graphics, identity)
  if (issue) throw new GraphicsRequirementsError([issue])
}

/** 校验一组主体，并在任何生命周期副作用前一次性抛出全部问题。 */
export function assertGraphicsRequirementSet(
  subjects: readonly {
    readonly subject: GraphicsRequirementDeclaration
    readonly identity: GraphicsRequirementSubjectIdentity
  }[],
  graphics: GraphicsBackend
): void {
  const issues = collectGraphicsRequirementIssues(subjects, graphics)
  if (issues.length > 0) throw new GraphicsRequirementsError(issues)
}

/**
 * 在创建 backend 前按插件注册顺序合并静态 device requirements。
 *
 * 只有显式支持 WebGPU 的主体会参与；未声明旧插件仍由后续 graphics gate 汇总拒绝。
 */
export function collectWebGPUDeviceRequirements(
  subjects: readonly GraphicsRequirementDeclaration[]
): ResolvedWebGPUDeviceRequirements {
  const features = new Set<WebGPUDeviceFeatureName>()
  const limits = new Map<WebGPUDeviceLimitName, { minimum?: number; maximum?: number }>()

  for (const subject of subjects) {
    if (!resolveDeclaration(subject).backends.includes('webgpu')) continue
    for (const feature of subject.webgpuDeviceRequirements?.features ?? []) {
      features.add(feature)
    }
    for (const requirement of subject.webgpuDeviceRequirements?.limits ?? []) {
      const minimum = requirement.minimum
      const maximum = requirement.maximum
      if (
        (minimum !== undefined && (!Number.isFinite(minimum) || minimum < 0)) ||
        (maximum !== undefined && (!Number.isFinite(maximum) || maximum < 0)) ||
        (minimum === undefined && maximum === undefined)
      ) {
        throw new GraphicsConfigConflictError(
          `无效 WebGPU device limit requirement：${requirement.name}`,
          [`webgpuDeviceRequirements.limits.${requirement.name}`]
        )
      }
      const current = limits.get(requirement.name) ?? {}
      const next = {
        minimum:
          minimum === undefined
            ? current.minimum
            : Math.max(current.minimum ?? Number.NEGATIVE_INFINITY, minimum),
        maximum:
          maximum === undefined
            ? current.maximum
            : Math.min(current.maximum ?? Number.POSITIVE_INFINITY, maximum),
      }
      if (next.minimum !== undefined && next.maximum !== undefined && next.minimum > next.maximum) {
        throw new GraphicsConfigConflictError(
          `WebGPU device limit requirement 冲突：${requirement.name} 的 minimum=${next.minimum} 大于 maximum=${next.maximum}`,
          [`webgpuDeviceRequirements.limits.${requirement.name}`]
        )
      }
      limits.set(requirement.name, next)
    }
  }

  if (features.size === 0 && limits.size === 0) return EMPTY_DEVICE_REQUIREMENTS
  return Object.freeze({
    features: Object.freeze(Array.from(features)),
    limits: Object.freeze(
      Array.from(limits, ([name, bounds]) => Object.freeze({ name, ...bounds }))
    ),
  })
}

/** 根据 adapter 快照验证静态要求并生成 requestDevice descriptor。 */
export function resolveWebGPUDeviceRequest(
  requirements: ResolvedWebGPUDeviceRequirements,
  adapterFeatures: { has(name: string): boolean },
  adapterLimits: Readonly<Record<string, unknown>> | undefined,
  optionalFeatures: readonly WebGPUDeviceFeatureName[] = []
): WebGPUDeviceRequestResolution {
  const requiredFeatures = new Set<WebGPUDeviceFeatureName>()
  const missingRequirements: string[] = []
  for (const feature of requirements.features) {
    if (!adapterFeatures.has(feature)) {
      missingRequirements.push(`device-feature:${feature}`)
    } else {
      requiredFeatures.add(feature)
    }
  }
  for (const feature of optionalFeatures) {
    if (adapterFeatures.has(feature)) requiredFeatures.add(feature)
  }

  const requiredLimits: Partial<Record<WebGPUDeviceLimitName, number>> = {}
  for (const requirement of requirements.limits) {
    const rawActual = adapterLimits?.[requirement.name]
    const actual = typeof rawActual === 'number' ? rawActual : undefined
    if (!isLimitSatisfied(actual, requirement)) {
      missingRequirements.push(formatLimitRequirement(requirement))
      continue
    }
    requiredLimits[requirement.name] = requirement.minimum ?? actual
  }

  return Object.freeze({
    requiredFeatures: Object.freeze(Array.from(requiredFeatures)),
    requiredLimits: Object.freeze(requiredLimits),
    missingRequirements: Object.freeze(missingRequirements),
  })
}
